import api from '@anm/api';
import { Webinar } from '@anm/api/modules/hosting';
import { ProjectMeta } from '@anm/api/modules/uploads';
import { Action } from '@anm/components/ActionsDropdown';
import StreamVideoAction from '@anm/components/dropdown-actions/streamVideo';
import { HOUR_MS, MINUTE_MS, SECOND_MS } from '@anm/constants/date';
import { STREAMING_LIVE_API_URL_PROD, STREAMING_LIVE_API_URL_TEST } from '@anm/constants/streaming';
import downloadWebinarAttendees from '@anm/helpers/downloadWebinarAttendees';
import downloadWebinarComments from '@anm/helpers/downloadWebinarComments';
import { getDropdownAction, getDropdownActions } from '@anm/helpers/dropdownActions';
import getFromCdn from '@anm/helpers/getFromCdn';
import isDev from '@anm/helpers/is/isDev';
import isMobile from '@anm/helpers/is/isMobile';
import * as semrush from '@anm/helpers/semrush';
import getIsPastStream from '@anm/helpers/stream/getIsPastStream';
import { getLinkTarget } from '@anm/helpers/upgradeLink';
import useHover from '@anm/hooks/useHover';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveStream } from 'types/stream';

import { getGoingLiveBadge } from './helpers';
import { StreamBadge, StreamStatus, UseDropdownActionsProps } from './types';

type StreamVideoActionProps = {
  isUserFree: boolean;
  handler?: () => void;
};

const useStreamVideoAction = ({ isUserFree, handler }: StreamVideoActionProps) => {
  const canStream = !isUserFree;

  const action = getDropdownAction('onStreamVideo');

  const streamVideoAction: Action = {
    icon: action.icon,
    disabled: !canStream,
    autoClose: canStream,
    renderComponent: () => <StreamVideoAction {...{ action, handler, isUserFree }} canStreamVideo={canStream} />
  };

  return streamVideoAction;
};

export const useDropdownActions = ({
  kind,
  state,
  canDebug,
  streamId,
  webinarId,
  isUserFree,
  projectLink,
  hasDestinations,
  onDelete,
  onDuplicate,
  onStreamVideo,
  onDownloadVideo,
  openDeleteModal,
  onOpenEditModal,
  onEditDestinations,
  openStopStreamModal,
  onOpenRecordingFolder,
  onCreateProjectFromRecording
}: UseDropdownActionsProps) => {
  const [webinar, setWebinar] = useState<Webinar>();

  useEffect(() => {
    (async () => {
      if (!webinar && webinarId) {
        const wbnr = (await api().hosting.getLandingPage({ hostId: webinarId })) as Webinar;

        wbnr && setWebinar(wbnr);
      }
    })();
  }, [webinar?.hostId, webinarId]);

  const isLive = kind === 'LIVE';
  const isHosted = kind === 'HOSTED';
  const isRecording = kind === 'RECORD';
  const isScheduled = state === 'SCHEDULED';
  const isFinished = state === 'FINISHED';
  const canStopStream = state === 'INPROGRESS';
  const canShowEdit = !!onOpenEditModal && isScheduled;
  const canDelete = onDelete && state !== 'INPROGRESS';
  const isPastStream = getIsPastStream(state);
  const canDownloadVideo = !isHosted && isPastStream;
  const isEditDestinations = isScheduled && isLive && !!onEditDestinations && isMobile();
  const canShowDownloadAttendee = !!(isFinished && webinarId && webinar?.parameters.access?.nameEmail);
  const isUpcomingLive = isLive && isScheduled;
  const canRemoveFromWave = canDelete && !isUpcomingLive;
  const canRemoveFromAllDestinations = canDelete && (hasDestinations || webinarId);
  const canDownloadComments = webinarId && isPastStream;

  const isSemrush = semrush.isSemrushIframe();
  const streamVideoAction = useStreamVideoAction({ isUserFree, handler: onStreamVideo });

  const debugBaseUrl = isDev() ? STREAMING_LIVE_API_URL_TEST : STREAMING_LIVE_API_URL_PROD;

  const debugAction: Action = {
    renderLink: () => (
      <a href={`${debugBaseUrl}debugging/detail/${streamId}`}>
        <span>Debug</span>
      </a>
    )
  };

  const actions = [
    isPastStream && {
      icon: getFromCdn('images/dashboard/player-btn-gray-v2.svg'),
      renderLink: () => (
        <a href={projectLink} target={getLinkTarget()}>
          <span>Open video</span>
        </a>
      )
    },
    isPastStream && streamVideoAction,
    canDownloadVideo && {
      name: 'Download video',
      icon: getFromCdn('images/icons/download_icon_grey_V1.svg'),
      handler: onDownloadVideo
    },
    canShowEdit && {
      name: 'Edit settings',
      icon: getFromCdn('images/dashboard/edit_v2.svg'),
      handler: onOpenEditModal
    },
    isEditDestinations && {
      name: 'Edit destinations',
      icon: getFromCdn('images/icons/destinations.svg'),
      handler: onEditDestinations
    },
    canShowDownloadAttendee && {
      name: 'Download attendees',
      handler: () => downloadWebinarAttendees(streamId)
    },
    canDownloadComments && {
      name: 'Download webinar comments',
      icon: getFromCdn('images/icons/download_icon_grey_V1.svg'),
      handler: () => downloadWebinarComments(streamId)
    },
    onOpenRecordingFolder && {
      name: 'Open folder with recordings',
      handler: onOpenRecordingFolder
    },
    onCreateProjectFromRecording && {
      name: 'Create project from recordings',
      handler: onCreateProjectFromRecording
    },
    ...getDropdownActions({
      onDuplicate
    }),
    canRemoveFromWave && {
      name: isSemrush ? 'Remove from platform' : 'Remove from wave.video',
      icon: getFromCdn('images/dashboard/cut-split.svg'),
      handler: () => openDeleteModal({ isDeleteFromWave: true })
    },
    canRemoveFromAllDestinations && {
      name: 'Delete from all destinations',
      icon: getFromCdn('images/dashboard/delete.svg'),
      handler: () => openDeleteModal({ isDeleteFromWave: false })
    },
    canStopStream && {
      name: isRecording ? 'Stop recording' : 'Stop stream',
      icon: getFromCdn('images/streaming/stop-red-ic.svg'),
      handler: openStopStreamModal
    },
    canDebug && debugAction
  ].filter(Boolean) as Action[];

  return actions;
};

const badgeMap: Record<StreamStatus, StreamBadge | null> = {
  live: {
    icon: 'images/streaming/live-red-ic.svg',
    variant: 'light-red',
    text: 'Live stream'
  },
  recording: {
    icon: 'images/streaming/recording-blue.svg',
    variant: 'blue',
    text: 'Recording'
  },
  hosted: {
    icon: 'images/streaming/pre-recorded-icon.svg',
    variant: 'green',
    text: 'Pre-recorded'
  },
  failed: {
    icon: 'images/streaming/warning.svg',
    variant: 'orange',
    text: 'Failed'
  },
  canceled: {
    icon: 'images/streaming/warning.svg',
    variant: 'orange',
    text: 'Canceled'
  },
  paused: {
    icon: 'images/streaming/pause-icon.svg',
    variant: 'orange',
    text: 'Paused'
  },
  default: null
};

const FIVE_MINS = MINUTE_MS * 5;

type StreamBadgeProps = Pick<LiveStream, 'schedule' | 'kind'> & {
  status: StreamStatus;
};

export const useStreamBadge = ({ schedule, kind, status }: StreamBadgeProps): StreamBadge => {
  const timeDiff = schedule ? new Date(schedule).getTime() - Date.now() : 0;
  const [isLessThanHourToStart, setIsLessThanHourToStart] = useState(timeDiff < HOUR_MS);
  const isLessThan5MinsToStart = timeDiff < FIVE_MINS;

  const [badge, setBadge] = useState<StreamBadge>(badgeMap[status]);
  const [isBadgeWithCountdown, setIsBadgeWithCountdown] = useState(false);

  const calcNewBadge = useCallback(() => {
    const timeDiff = schedule ? new Date(schedule).getTime() - Date.now() : 0;
    const timeInSeconds = ~~(timeDiff / 1000);

    const newBadge = timeDiff > 0 ? getGoingLiveBadge(timeInSeconds) : badgeMap[status];

    if (badge?.text !== newBadge?.text) {
      setBadge(newBadge);
    }
  }, [schedule, badge?.text]);

  let timer: number;
  let minTimer: number;
  let secTimer: number;

  const showCountdownIn = useRef<number | null>(null);

  useEffect(() => {
    const timeDiff = schedule ? new Date(schedule).getTime() - Date.now() : 0;
    const isCountdown = !!schedule && kind !== 'RECORD' && timeDiff < HOUR_MS;
    setIsBadgeWithCountdown(isCountdown);
  }, [schedule, isLessThanHourToStart]);

  useEffect(() => {
    const timeDiff = schedule ? new Date(schedule).getTime() - HOUR_MS - Date.now() : 0;

    if (timeDiff > 0) {
      showCountdownIn.current = timeDiff;
    }
  }, [schedule]);

  useEffect(() => {
    if (!showCountdownIn.current) return clearTimeout(timer);

    timer = window.setTimeout(() => {
      setIsLessThanHourToStart(true);
    }, showCountdownIn.current);

    () => {
      clearTimeout(timer);
    };
  }, [showCountdownIn.current]);

  useEffect(() => {
    if (!isBadgeWithCountdown || isLessThan5MinsToStart) return clearTimeout(minTimer);
    calcNewBadge();
    minTimer = window.setTimeout(calcNewBadge, MINUTE_MS);

    return () => {
      clearTimeout(minTimer);
    };
  }, [badge, isBadgeWithCountdown, isLessThan5MinsToStart, schedule]);

  useEffect(() => {
    if (!isBadgeWithCountdown || !isLessThan5MinsToStart) return clearTimeout(secTimer);
    secTimer = window.setTimeout(calcNewBadge, SECOND_MS);

    return () => {
      clearTimeout(secTimer);
    };
  }, [badge, isBadgeWithCountdown, isLessThan5MinsToStart, schedule]);

  const badgeOrNull = isBadgeWithCountdown ? badge : status ? badgeMap[status] : null;

  return badgeOrNull;
};

export const useDropdownOpenDirection = (actionLength: number, isFirstCard?: boolean) => {
  const [isTop, setIsTop] = useState(false);
  const [actionsRef, isHovered] = useHover<HTMLDivElement>();

  const distanceToBottom = window.innerHeight - (actionsRef.current?.getBoundingClientRect().bottom || 0);

  useEffect(() => {
    setIsTop(distanceToBottom < actionLength * 45);
  }, [isHovered]);

  const dropdownDirection = isTop && !isFirstCard ? 'to-top-low-tunnel' : 'to-bottom-left-low-tunnel';

  return [actionsRef, dropdownDirection] as const;
};

export type DeleteProp = {
  isDeleteFromWave: boolean;
};

export const useShowDeleteModal = (
  hasFile: boolean,
  fetchUploadProjects: () => void,
  uploadProjects?: ProjectMeta[]
) => {
  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useSwitchState();
  const [showDeleteModalOpen, showDeleteModal, hideDeleteModal] = useSwitchState(!hasFile);
  const [isDeleteFromWave, setIsDeleteFromWave] = useState(false);

  const isOpenModal = isDeleteModalOpen && showDeleteModalOpen;

  useEffect(() => {
    if (isDeleteModalOpen) showDeleteModal();
  }, [uploadProjects]);

  const handelOpenDeleteModal = useCallback(({ isDeleteFromWave }: DeleteProp) => {
    setIsDeleteFromWave(isDeleteFromWave);

    openDeleteModal();
    fetchUploadProjects();
  }, []);

  const handelCloseDeleteModal = useCallback(() => {
    hasFile && hideDeleteModal();
    closeDeleteModal();
  }, [hasFile]);

  return [isOpenModal, handelOpenDeleteModal, handelCloseDeleteModal, isDeleteFromWave] as const;
};

export const useStreamErrors = (streamError?: string) => {
  const [destinationsWithError, setDestinationErrors] = useState({} as Record<string, string>);
  const [isStreamError, setStreamError] = useSwitchState();

  useEffect(() => {
    if (!streamError) return;

    try {
      const jsonPart = streamError?.split('{"')[1] || '';
      const jsonString = `{"${jsonPart}`;
      const canParse = jsonString.length > 2 && jsonPart;
      const errors = (canParse ? JSON.parse(jsonString) : {}) as Record<string, string>;

      setDestinationErrors(errors);
      jsonString.length === 2 && setStreamError();
    } catch (e) {
      console.error('error parsing', streamError, e);
    }
  }, [streamError]);

  return [isStreamError, destinationsWithError] as const;
};
